import React from 'react';
import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import type { Flag } from './ClientServerGraph.types';

const FLAG_ELEMENTS = [
  [
    <motion.path
      layout
      key="shortest-flag-fill"
      layoutId="shortest-flag-fill"
      fill="var(--color-background)"
      d="M12.5 75.448c-.335-3.178-5.998-4.858-9.25-5.565C2.067 69.626 1 70.548 1 71.76v10.948s12-2.51 11.5-7.26z"
    />,
    <motion.path
      layout
      key="shortest-flag-stroke"
      layoutId="shortest-flag-stroke"
      stroke="var(--color-text)"
      strokeLinecap="round"
      strokeWidth="2"
      d="M1 422V82.708m0 0V71.76c0-1.212 1.066-2.134 2.25-1.877 3.252.707 8.915 2.387 9.25 5.565.5 4.75-11.5 7.26-11.5 7.26z"
    />,
  ],
  [
    <motion.path
      layout
      key="middle-flag-fill"
      layoutId="middle-flag-fill"
      fill="var(--color-background)"
      d="M12.5 41.281c-.335-3.177-5.998-4.858-9.25-5.564-1.184-.258-2.25.665-2.25 1.877v10.948s12-2.51 11.5-7.26z"
    />,
    <motion.path
      layout
      key="tallest-flag-stroke"
      layoutId="tallest-flag-stroke"
      stroke="var(--color-text)"
      strokeLinecap="round"
      strokeWidth="2"
      d="M1 422V48.542m0 0V37.594c0-1.212 1.066-2.135 2.25-1.877 3.252.706 8.915 2.387 9.25 5.564.5 4.751-11.5 7.26-11.5 7.26z"
    />,
  ],
  [
    <motion.path
      layout
      key="tallest-flag-fill"
      layoutId="tallest-flag-fill"
      fill="var(--color-background)"
      d="M12.5 7.115c-.335-3.178-5.998-4.858-9.25-5.565C2.067 1.292 1 2.215 1 3.427v10.948s12-2.51 11.5-7.26z"
    />,
    <motion.path
      layout
      key="tallest-flag-stroke"
      layoutId="tallest-flag-stroke"
      stroke="var(--color-text)"
      strokeLinecap="round"
      strokeWidth="2"
      d="M1 422V14.375m0 0V3.427C1 2.215 2.066 1.292 3.25 1.55c3.252.707 8.915 2.387 9.25 5.565.5 4.75-11.5 7.26-11.5 7.26z"
    />,
  ],
];

const DISTANCE_BETWEEN_FLAGS = 34;

interface Props {
  formattedFlags: Array<Flag>;
  columnWidth: number;
}

function Flags({ formattedFlags, columnWidth }: Props) {
  return (
    <Svg
      width="14"
      height="423"
      fill="none"
      viewBox="0 0 14 423"
      xmlns="http://www.w3.org/2000/svg"
    >
      {formattedFlags.map((flag) => {
        return (
          <Flag key={flag.id} flag={flag} columnWidth={columnWidth} />
        );
      })}
    </Svg>
  );
}

function Flag({
  flag,
  columnWidth,
}: {
  flag: Flag;
  columnWidth: number;
}) {
  const [isTransitioning, setIsTransitioning] = React.useState(false);

  const flagTimeRef = React.useRef(flag.time);

  const textNodes = flag.label.split('\n');

  // The text Y position is calculated based on the
  // number of flags in this group.
  const dy =
    DISTANCE_BETWEEN_FLAGS * 2 -
    flag.stackIndex * DISTANCE_BETWEEN_FLAGS -
    2;

  React.useEffect(() => {
    if (flag.time !== flagTimeRef.current) {
      flagTimeRef.current = flag.time;
      setIsTransitioning(true);

      const timeoutId = window.setTimeout(() => {
        setIsTransitioning(false);
      }, 300);

      return () => window.clearTimeout(timeoutId);
    }
  }, [flag]);

  return (
    <FlagWrapper
      data-is-sliding-around={String(isTransitioning)}
      style={{
        opacity: isTransitioning ? 0.25 : 1,
        transform: `translate(${flag.time * columnWidth - 1}px, 1px)`,
      }}
    >
      {FLAG_ELEMENTS[flag.stackIndex]}
      {textNodes.map((textNode, textNodeIndex) => {
        return (
          <motion.text
            layout
            key={textNodeIndex}
            x={20}
            y={textNodeIndex * 12 + 8}
            dy={dy}
            fill="var(--color-text)"
            fontSize="12"
          >
            {textNode}
          </motion.text>
        );
      })}
    </FlagWrapper>
  );
}

const Svg = styled.svg`
  display: block;
  position: absolute;
  bottom: 0;
  left: 0;
  overflow: visible;
  animation: fadeIn 1000ms;
`;

const FlagWrapper = styled.g`
  transition:
    transform 400ms,
    opacity 600ms;

  &[data-is-sliding-around='true'] {
    transition:
      transform 400ms,
      opacity 200ms;
  }
`;

export default Flags;
